// Render the actual building geometry into small, cached build-menu portraits.
// One temporary WebGL context renders the whole set, then releases its resources.
import {
  AmbientLight, Box3, DirectionalLight, DoubleSide, Group, Mesh,
  MeshLambertMaterial, OrthographicCamera, Scene, Vector3, WebGLRenderer,
} from 'three'
import type { BuildingType } from '../../shared/types'
import { buildingGeometry } from '../render3d/models3d/buildings3d'

export function createBuildingPreviews(types: BuildingType[]): Map<BuildingType, string> {
  const previews = new Map<BuildingType, string>()
  let renderer: WebGLRenderer | undefined
  const material = new MeshLambertMaterial({ vertexColors: true, flatShading: true, side: DoubleSide })
  try {
    renderer = new WebGLRenderer({ alpha: true, antialias: true })
    renderer.setSize(192, 144)
    renderer.setClearColor(0, 0)
    const scene = new Scene()
    scene.add(new AmbientLight(0xffefd5, 2))
    const sun = new DirectionalLight(0xfff2d2, 2.6)
    sun.position.set(-50, 100, -70)
    scene.add(sun)
    const camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 2000)
    const group = new Group()
    scene.add(group)
    for (const type of types) {
      if (['road', 'chop', 'dig', 'fill', 'fillsand'].includes(type)) continue
      const mesh = new Mesh(buildingGeometry(type, '#78a7a0', 0), material)
      group.add(mesh)
      const bounds = new Box3().setFromObject(mesh)
      const center = bounds.getCenter(new Vector3())
      const radius = bounds.getSize(new Vector3()).length() / 2
      camera.position.copy(center).add(new Vector3(1, 0.85, -1.2).normalize().multiplyScalar(radius * 4))
      camera.lookAt(center)
      camera.left = -radius * 1.2; camera.right = radius * 1.2
      camera.top = radius * 0.9; camera.bottom = -radius * 0.9
      camera.updateProjectionMatrix()
      renderer.render(scene, camera)
      previews.set(type, renderer.domElement.toDataURL('image/png'))
      group.remove(mesh)
    }
  } catch {
    // Canvas-only devices keep the existing illustrated glyphs.
  } finally {
    material.dispose()
    renderer?.dispose()
    renderer?.forceContextLoss()
  }
  return previews
}
